import { createFileRoute, Outlet, redirect } from "@tanstack/react-router";

import {
  organizationQueryOptions,
  staffStatusQueryOptions,
  userOrganizationsQueryOptions,
} from "@mountayaapp/api";

import { identifyActiveOrganization } from "../analytics/rules/identify";
import { env } from "../env";
import { OrgBannerStack } from "../iam/organization/components/OrgBannerStack";
import { IamPageContainer } from "../iam/shared/components/IamPageContainer";
import { OrgPageShell } from "../iam/shared/components/OrgPageShell";
import { writeRememberedOrganizationId } from "../iam/shared/rules/rememberedOrganization";
import {
  findActiveMembership,
  resolveOrganizationAccess,
} from "../iam/staff/rules/organizationAccess";

/**
 * Route is /organizations/$organizationId: the per-organization guard layout. Its beforeLoad
 * runs render-free (mirroring _app's guard) and decides how the viewer holds the organization:
 *
 * - through an accepted membership (status "active"), read off the shared userOrganizations
 *   cache; an unaccepted invitation or a not-a-member id is no membership;
 * - or, without one, as Mountaya staff: the staff status read says so, and the organization is
 *   read through the staff read waiver for its name. A refused read (the personal-accounts
 *   organization, an unknown or canceled id) is no way in.
 *
 * No way in redirects to /organizations. The memberships and the staff status are read in
 * parallel from the query cache, so a navigation between subpages waits on nothing; a staff
 * answer older than the standard window is asked again in the background.
 *
 * A member's visit is recorded as the active organization in the identity store and
 * remembered for the next visit, and re-scopes analytics to the organization, but only on a
 * real navigation, never a preload. A staff visit does none of the three: the customer's
 * organization is not the staff member's own, and their reading is not the customer's usage.
 *
 * The access rides the router context, so every subpage reads it through useOrganizationAccess
 * without a refetch. The component mounts the always-on organization banner stack edge to edge
 * (outside the page container padding) above the IAM container + the org page shell.
 */
export const Route = createFileRoute("/_app/organizations/$organizationId")({
  beforeLoad: async ({ context, params, preload }) => {
    const config = { baseUrl: env.apiBaseUrl };
    // The _app guard above has already refused a visitor without a session, so the
    // signed-in user is known here; without one there is no way in at all.
    const userId = context.session?.userId;
    const [memberships, staff] = await Promise.all([
      userId
        ? context.queryClient
            .ensureQueryData(userOrganizationsQueryOptions(config, userId))
            .catch(() => [])
        : [],
      // The staff answer can move under a session that stays signed in, so one older than the
      // standard window is asked again in the background rather than trusted for the session.
      userId
        ? context.queryClient
            .ensureQueryData({ ...staffStatusQueryOptions(config), revalidateIfStale: true })
            .then((status) => status.staff)
            .catch(() => false)
        : false,
    ]);

    // Only a staff member who does not belong reads the organization here: a member's
    // membership already names it, and anybody else would be refused the read.
    const staffReadName =
      staff && findActiveMembership(memberships, params.organizationId) === null
        ? await context.queryClient
            .ensureQueryData(organizationQueryOptions(config, params.organizationId))
            .then((organization) => organization.name)
            .catch(() => null)
        : null;

    const access = resolveOrganizationAccess({
      memberships,
      organizationId: params.organizationId,
      staff,
      staffReadName,
    });

    if (!access) {
      throw redirect({ to: "/organizations" });
    }

    // Record the active organization in the identity store + remembered id, but only on
    // a real navigation — never a preload. beforeLoad also runs on hover preload
    // (defaultPreload "intent"), and writing the active org on preload would let merely
    // hovering an organization in the switcher mark it current on the account pages,
    // where no route id pins the resolver. The bus is not mounted at route-load time, so
    // the guard writes the store directly — the same boot-time seam ensureSession and the
    // embed route use. The getState() handle is taken into a local so the setOrganization
    // call is its own statement.
    if (!preload && access.membership !== null) {
      const identityState = context.identityStore.getState();
      identityState.setOrganization(params.organizationId);
      writeRememberedOrganizationId(params.organizationId);

      // The same navigation re-scopes analytics to this organization, so events
      // captured from here on are attributed to the customer, not only to the
      // person. Landing straight on an org page runs this after the _app guard,
      // which is where the org group first gets set for a fresh page load.
      identifyActiveOrganization(params.organizationId, memberships);
    }

    return { access };
  },
  component: OrganizationLayout,
});

function OrganizationLayout() {
  const { access } = Route.useRouteContext();

  return (
    <>
      {/* The banner stack mounts OUTSIDE the page container so its rows span the
          content column edge to edge; the container below restores the page gutter
          for the routed body. */}
      <OrgBannerStack organizationId={access.organizationId} />
      <IamPageContainer>
        <OrgPageShell access={access}>
          <Outlet />
        </OrgPageShell>
      </IamPageContainer>
    </>
  );
}
